import { Fragment, useEffect, useRef, useState, useMemo, FC } from 'react';
import { FacebookIcon } from '@ui/icons/social/facebook';
import { InstagramIcon } from '@ui/icons/social/instagram';
import { YoutubeIcon } from '@ui/icons/social/youtube';
import { LandingNavbar } from '@app/landing/landing-navbar';
import { color, motion } from "framer-motion";
import { Sparkles, User } from 'lucide-react';
import { useThemeSelector } from '@ui/themes/theme-selector-context';
import { Helmet } from "@common/seo/helmet";
export function Solution() {

const { selectThemeTemporarily } = useThemeSelector();
useEffect(() => {
selectThemeTemporarily('light');
return () => selectThemeTemporarily(null);
}, [selectThemeTemporarily]);

const CSS = `

  :root {
    --blue: #1a56db;
    --blue-dark: #1039a8;
    --blue-mid: #2563eb;
    --blue-light: #dbeafe;
    --blue-xlight: #eff6ff;
    --cyan: #0891b2;
    --cyan-light: #cffafe;
    --slate-50: #f8fafc;
    --slate-100: #f1f5f9;
    --slate-200: #e2e8f0;
    --slate-300: #cbd5e1;
    --slate-400: #94a3b8;
    --slate-500: #64748b;
    --slate-600: #475569;
    --slate-700: #334155;
    --slate-800: #1e293b;
    --slate-900: #0f172a;
    --white: #ffffff;
    --green: #059669;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }

  body {
    font-family: var(--font-body);
    background: var(--white);
    color: var(--slate-700);
    font-size: 16px;
    line-height: 1.6;
    overflow-x: hidden;
  }

 
  .sb-btn { display: inline-flex; align-items: center; gap: 7px; text-decoration: none; border-radius: 8px; font-family: var(--font-body); font-weight: 600; font-size: 0.85rem; cursor: pointer; border: none; transition: all 0.2s; }
  .sb-btn-sm { padding: 9px 18px; }
  .sb-btn-md { padding: 12px 24px; }
  .sb-btn-lg { padding: 15px 30px; font-size: 0.95rem; }
  .sb-btn-primary { background: var(--blue); color: white; box-shadow: 0 2px 10px rgba(26,86,219,0.28); }
  .sb-btn-primary:hover { background: var(--blue-dark); transform: translateY(-1px); box-shadow: 0 6px 20px rgba(26,86,219,0.36); }
  .sb-btn-outline { background: white; color: var(--blue); border: 1.5px solid var(--blue-light); }
  .sb-btn-outline:hover { background: var(--blue-xlight); border-color: var(--blue); }
  .sb-btn-white { background: white; color: var(--blue-dark); box-shadow: 0 2px 12px rgba(0,0,0,0.15); }
  .sb-btn-white:hover { background: var(--blue-xlight); transform: translateY(-1px); }
  .sb-btn-ghost-white { background: rgba(255,255,255,0.12); color: white; border: 1.5px solid rgba(255,255,255,0.3); }
  .sb-btn-ghost-white:hover { background: rgba(255,255,255,0.22); }

  .sb-breadcrumb {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.8rem;
    color: rgba(255,255,255,0.6);
    margin-bottom: 24px;
    position: relative;
  }
  .sb-breadcrumb a { color: rgba(255,255,255,0.6); text-decoration: none; }
  .sb-breadcrumb a:hover { color: white; }
  .sb-breadcrumb .sep { color: rgba(255,255,255,0.3); }
  .sb-breadcrumb .current { color: rgba(255,255,255,0.9); font-weight: 500; }


  /* PAGE BODY */
  .sb-page-body {
    max-width: 1230px;
    margin: 0 auto;
    padding: 56px 32px;
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 56px;
    align-items: start;
  }

  /* SIDEBAR */
  .sb-sidebar { position: sticky; top: 88px; }
  .sb-sidebar-heading {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--slate-400);
    padding: 0 16px 10px;
    border-bottom: 1px solid var(--slate-200);
    margin-bottom: 8px;
  }
  .sb-sidebar-nav { list-style: none; }
  .sb-sidebar-nav li a {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 16px;
    border-radius: 10px;
    text-decoration: none;
    color: var(--slate-600);
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.18s;
    border-left: 2px solid transparent;
  }
  .sb-sidebar-nav li a:hover,
  .sb-sidebar-nav li a.active {
    color: var(--blue);
    background: var(--blue-xlight);
    border-left-color: var(--blue);
    font-weight: 600;
  }
  .sb-nav-icon {
    width: 28px; height: 28px;
    border-radius: 7px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .sb-nav-icon svg { width: 15px; height: 15px; stroke-width: 2; fill: none; }

  .sb-sidebar-cta {
    margin-top: 28px;
    background: linear-gradient(135deg, var(--blue), var(--cyan));
    border-radius: 14px;
    padding: 24px 20px;
    text-align: center;
    color: white;
  }
  .sb-sidebar-cta .icon { font-size: 2rem; margin-bottom: 10px; }
  .sb-sidebar-cta h4 {
    font-family: var(--font-display);
    font-size: 1.05rem;
    font-weight: 700;
    margin-bottom: 6px;
  }
  .sb-sidebar-cta p { font-size: 0.78rem; opacity: 0.85; margin-bottom: 16px; line-height: 1.5; }
  .sb-sidebar-cta a {
    display: block;
    background: white;
    color: var(--blue);
    font-weight: 700;
    font-size: 0.82rem;
    padding: 10px;
    border-radius: 8px;
    text-decoration: none;
    transition: 0.2s;
  }
  .sb-sidebar-cta a:hover { background: var(--blue-xlight); }

  /* MAIN */
  .sb-main { min-width: 0; }

  .sb-section-intro { margin-bottom: 48px; }
  .sb-section-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--blue);
    margin-bottom: 12px;
  }
  .sb-section-label::before {
    content: '';
    display: block;
    width: 20px; height: 2px;
    background: var(--blue);
    border-radius: 2px;
  }
  .sb-section-intro h2 {
    font-family: var(--font-display);
    font-size: clamp(1.9rem, 3vw, 2.6rem);
    font-weight: 700;
    color: var(--slate-900);
    line-height: 1.2;
    margin-bottom: 14px;
  }
  .sb-section-intro p {
    font-size: 1rem;
    color: var(--slate-600);
    max-width: 640px;
    line-height: 1.75;
  }

  /* SOLUTION BLOCKS */
  .sb-solution-block {
    margin-bottom: 72px;
    scroll-margin-top: 100px;
  }
  .sb-solution-block:last-child { margin-bottom: 0; }
  .sb-sol-inner {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 44px;
    align-items: center;
  }
  .sb-sol-inner.reverse { direction: rtl; }
  .sb-sol-inner.reverse > * { direction: ltr; }

  .sb-sol-image {
    position: relative;
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 16px 48px rgba(0,0,0,0.12);
    aspect-ratio: 4/3;
    background: var(--slate-100);
  }
  .sb-sol-image img {
    width: 100%; height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.5s ease;
  }
  .sb-solution-block:hover .sb-sol-image img { transform: scale(1.03); }
  .sb-sol-overlay {
    position: absolute; inset: 0;
    background: linear-gradient(to top, rgba(15,31,92,0.45) 0%, transparent 55%);
  }
  .sb-sol-image-tag {
    position: absolute;
    bottom: 16px; left: 16px;
    background: white;
    border-radius: 10px;
    padding: 8px 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }
  .sb-tag-icon {
    width: 32px; height: 32px;
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
  }
  .sb-tag-text { font-size: 0.78rem; font-weight: 700; color: var(--slate-800); line-height: 1.1; }
  .sb-tag-sub { font-size: 0.66rem; color: var(--slate-500); }

  .sb-sol-content { padding: 8px 0; }
  .sb-sol-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--blue-xlight);
    border: 1px solid var(--blue-light);
    color: var(--blue);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 5px 12px;
    border-radius: 100px;
    margin-bottom: 14px;
  }
  .sb-sol-content h2 {
    font-family: var(--font-display);
    font-size: clamp(1.5rem, 2.5vw, 1.95rem);
    font-weight: 700;
    color: var(--slate-900);
    line-height: 1.25;
    margin-bottom: 14px;
  }
  .sb-sol-divider {
    width: 48px; height: 3px;
    background: linear-gradient(90deg, var(--blue), var(--cyan));
    border-radius: 3px;
    margin-bottom: 18px;
  }
  .sb-sol-content p {
    font-size: 0.95rem;
    color: var(--slate-600);
    line-height: 1.78;
    margin-bottom: 24px;
  }
  .sb-sol-features {
    list-style: none;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 28px;
  }
  .sb-sol-features li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 0.85rem;
    color: var(--slate-700);
    font-weight: 500;
  }
  .sb-chk {
    width: 18px; height: 18px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--blue), var(--cyan));
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .sb-chk svg { width: 9px; height: 9px; stroke: white; stroke-width: 2.5; fill: none; }
  .sb-sol-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--blue);
    font-weight: 600;
    font-size: 0.88rem;
    text-decoration: none;
    border-bottom: 1.5px solid var(--blue-light);
    padding-bottom: 2px;
    transition: all 0.2s;
  }
  .sb-sol-link:hover { border-bottom-color: var(--blue); gap: 12px; }
  .sb-sol-link svg { width: 16px; height: 16px; stroke-width: 2; fill: none; transition: transform 0.2s; }
  .sb-sol-link:hover svg { transform: translateX(4px); }

  .sb-block-divider {
    border: none;
    border-top: 1px solid var(--slate-200);
    margin-bottom: 72px;
  }

  /* REVEAL */
  .sb-reveal { opacity: 0; transform: translateY(24px); transition: opacity 0.6s ease, transform 0.6s ease; }
  .sb-reveal.visible { opacity: 1; transform: translateY(0); }

  /* RESPONSIVE */
  @media (max-width: 1024px) {
    .sb-page-body { grid-template-columns: 1fr; }
    .sb-sidebar { position: static; }
    .sb-sidebar-nav { display: flex; flex-wrap: wrap; gap: 8px; }
    .sb-sidebar-nav li a { padding: 8px 14px; font-size: 0.82rem; border-left: none; border-bottom: 2px solid transparent; border-radius: 8px; }
    .sb-sidebar-nav li a.active, .sb-sidebar-nav li a:hover { border-bottom-color: var(--blue); border-left-color: transparent; }
    .sb-sidebar-cta { display: none; }
  }
  @media (max-width: 768px) {
    .sb-nav-links { display: none; }
    .sb-sol-inner { grid-template-columns: 1fr; }
    .sb-sol-inner.reverse { direction: ltr; }
    .sb-sol-features { grid-template-columns: 1fr; }
    .sb-hero { padding: 52px 20px 60px; }
    .sb-page-body { padding: 40px 20px; }
  }
`;

const navItems = [
  { href: "#content-creators", label: "Content Creators", iconBg: "#eff6ff", iconStroke: "#1a56db", icon: <path d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/> },
  { href: "#photographers", label: "Photographers", iconBg: "#fdf4ff", iconStroke: "#9333ea", icon: <><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></> },
  { href: "#digital-marketing", label: "Digital Marketing", iconBg: "#fff7ed", iconStroke: "#d97706", icon: <path d="M18 20V10M12 20V4M6 20v-6"/> },
  { href: "#youtubers", label: "YouTubers", iconBg: "#fef2f2", iconStroke: "#dc2626", icon: <><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58 2.78 2.78 0 001.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.96A29 29 0 0023 12a29 29 0 00-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="#dc2626" stroke="none"/></> },
  { href: "#education", label: "Education", iconBg: "#f0fdf4", iconStroke: "#16a34a", icon: <><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></> },
  { href: "#video-creators", label: "Video Creators", iconBg: "#ecfdf5", iconStroke: "#059669", icon: <><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></> },
  { href: "#enterprise", label: "Enterprise", iconBg: "#eff6ff", iconStroke: "#1a56db", icon: <><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></> },
  { href: "#business", label: "Business Storage", iconBg: "#faf5ff", iconStroke: "#7c3aed", icon: <><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></> },
  { href: "#brand-creators", label: "Brand Creators", iconBg: "#fff1f2", iconStroke: "#e11d48", icon: <><circle cx="12" cy="12" r="10"/><path d="M8.56 2.75c4.37 6.03 6.02 9.42 8.03 17.72m2.54-15.38c-3.72 4.35-8.94 5.66-16.88 5.85m19.5 1.9c-3.5-.93-6.63-.82-8.94 0-2.58.92-5.01 2.86-7.44 6.32"/></> },
];

interface SolutionBlock {
  id: string;
  tag: string;
  title: string;
  body: string;
  features: string[];
  linkText: string;
  Text: string;
  link: string;
  reverse?: boolean;
  image: { src: string; alt: string };
  imageTagIcon: { bg: string; stroke: string; icon: React.ReactNode };
  imageTagText: string;
  imageTagSub: string;
}

const solutions: SolutionBlock[] = [
  {
    id: "content-creators",
    tag: "🎨 Content Creators",
    title: "Create Without Storage Limits",
    body: " Content creators handle videos, graphics, and project assets every day. SpaceByte cloud storage helps creators securely store, organize, and access creative files from anywhere, making project management easier while keeping content safe and always available.",
    features: ["Secure creative file storage", "Access from any device", "Smart project folders", "Instant client sharing"],
    linkText: "Start storing for free",
    Text: "Learn more",
    link: "/cloud-storage-for-content-creators",
    image: { src: "/images/solution/spacebyte-for-content-creators.webp", alt: "Cloud storage for content creators managing media files" },
    imageTagIcon: { bg: "#dbeafe", stroke: "#1a56db", icon: <path d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"/> },
    imageTagText: "All Files Synced",
    imageTagSub: "SpaceByte Cloud",
  },
  {
    id: "photographers",
    tag: "📷 Photographers",
    title: "Secure Storage for Every Shot",
    body: "Photographers manage thousands of high-resolution images and RAW files, while video creators handle large footage and editing projects. Brand creators also store campaign visuals and media kits. SpaceByte online cloud storage helps creators securely store, organize, and access content in one place while keeping files safe.",
    features: ["RAW & JPEG support", "Organize by shoot date", "Secure client galleries", "Auto photo backup"],
    linkText: "Protect your shots",
    link: "/cloud-storage-for-photographers",
    Text: "Learn more",
    reverse: true,
    image: { src: "/images/solution/spacebyte-for-photographers.webp", alt: "Photographer managing RAW photos in SpaceByte cloud storage" },
    imageTagIcon: { bg: "#fae8ff", stroke: "#9333ea", icon: <><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></> },
    imageTagText: "12,400+ Photos",
    imageTagSub: "Safely stored",
  },
  {
    id: "digital-marketing",
    tag: "📊 Digital Marketing",
    title: "Smarter Storage for Marketing Teams",
    body: "Digital marketing teams manage many files daily, including campaign creatives, performance reports, and analytics data. Social media managers handle graphics, videos, and content calendars, while SEO specialists work with audits, keyword research, and website reports. SpaceByte gives teams one place to keep everything organized, making collaboration easier and keeping important marketing data safe.",
    features: ["Campaign asset hub", "Team collaboration", "SEO report archive", "Secure client sharing"],
    linkText: "Organize your marketing",
    link: "/cloud-storage-for-digital-marketing",
    Text: "Learn more",
    image: { src: "/images/solution/spacebyte-for-digital-marketing.webp", alt: "Digital marketing files stored securely in SpaceByte cloud storage" },
    imageTagIcon: { bg: "#fff7ed", stroke: "#d97706", icon: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></> },
    imageTagText: "Team Workspace",
    imageTagSub: "Real-time sync",
  },
  {
    id: "youtubers",
    tag: "▶️ YouTubers",
    title: "Store Your Content Without Limits",
    body: "YouTubers often work with large video files, thumbnails, and editing projects that quickly take up device storage and make files harder to manage. SpaceByte cloud storage gives creators a reliable place to store, organize, and access their content anytime, keeping projects safe and easy to manage.",
    features: ["4K & 8K video ready", "Thumbnail management", "Channel-based folders", "Multi-device access"],
    linkText: "Grow your channel",
    link: "/cloud-storage-for-youtubers",
    Text: "Learn more",
    reverse: true,
    image: { src: "/images/solution/spacebyte-for-youtubers.webp", alt: "YouTuber storing video files in SpaceByte cloud storage" },
    imageTagIcon: { bg: "#fef2f2", stroke: "#dc2626", icon: <><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></> },
    imageTagText: "4K Storage Ready",
    imageTagSub: "Any file size",
  },
  {
    id: "education",
    tag: "🎓 Education",
    title: "Reliable Storage for Learning",
    body: "Schools, teachers, and students handle lots of digital files every day, from assignments and study notes to research materials. SpaceByte makes it easy to keep these files safe, organized in one place, and accessible whenever they’re needed for learning.",
    features: ["Safe assignment storage", "Classroom file sharing", "Any-device access", "Subject organization"],
    linkText: "Support your learning",
    link: "/cloud-storage-for-education",
    Text: "Learn more",
    image: { src: "/images/solution/spacebyte-for-education.webp", alt: "Students and teachers using SpaceByte cloud storage for assignments and study files" },
    imageTagIcon: { bg: "#f0fdf4", stroke: "#16a34a", icon: <><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></> },
    imageTagText: "Student Friendly",
    imageTagSub: "Free to start",
  },
  {
    id: "video-creators",
    tag: "🎞️ Video Creators",
    title: "Powerful Storage for Video Projects",
    body: "Video creators work with large footage, editing files, and media assets every day, which can quickly fill up local storage. SpaceByte provides online cloud storage, giving creators an easy way to store, organize, and access their video projects from anywhere, keeping files safe and projects running smoothly.",
    features: ["Unlimited footage storage", "Remote workstation access", "Project-based folders", "Client file delivery"],
    linkText: "Power your productions",
    link: "/cloud-storage-for-video-creators",
    Text: "Learn more",
    reverse: true,
    image: { src: "/images/solution/spacebyte-for-video-creators.webp", alt: "Video creator storing and accessing large video files using SpaceByte cloud storage" },
    imageTagIcon: { bg: "#ecfdf5", stroke: "#059669", icon: <><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></> },
    imageTagText: "Pro Video Storage",
    imageTagSub: "All formats",
  },
  {
    id: "enterprise",
    tag: "🏢 Enterprise",
    title: "Secure Storage for Enterprise Data",
    body: "Enterprises deal with large amounts of critical business information every day, which can be hard to manage and keep safe. SpaceByte offers scalable cloud storage for enterprises, helping teams keep critical business data safe, organized, and accessible whenever it’s needed.",
    features: ["Unlimited scalable storage", "Role-based access control", "Audit logs & compliance", "99.9% uptime SLA"],
    linkText: "Talk to our team",
    link: "/cloud-storage-for-enterprise",
    Text: "Learn more",
    image: { src: "/images/solution/spacebyte-for-enterprise.webp", alt: "Business team managing enterprise data with SpaceByte secure cloud storage" },
    imageTagIcon: { bg: "#eff6ff", stroke: "#1a56db", icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/> },
    imageTagText: "Enterprise Grade",
    imageTagSub: "256-bit encrypted",
  },
  {
    id: "business",
    tag: "💼 Business Storage",
    title: "Reliable Storage for Business Files",
    body: "Businesses deal with a lot of documents, reports, and internal data, and managing all of it can become messy over time. SpaceByte gives companies a reliable place to keep their files, making it easier for teams to find information, stay organized, and keep important business data safe.",
    features: ["Centralized file hub", "Smart file search", "Partner file sharing", "Automated backups"],
    linkText: "Organize your business",
    link: "/business-cloud-storage",
    Text: "Learn more",
    reverse: true,
    image: { src: "/images/solution/business-cloud-storage.webp", alt: "Business team storing and managing company files with SpaceByte cloud storage" },
    imageTagIcon: { bg: "#faf5ff", stroke: "#7c3aed", icon: <><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></> },
    imageTagText: "Business Ready",
    imageTagSub: "Secure & organized",
  },
  {
    id: "brand-creators",
    tag: "🎯 Brand Creators",
    title: "Protect Your Brand Assets",
    body: "Brand creators work with logos, design files, and many marketing assets that are important for maintaining a strong brand identity. SpaceByte keeps these materials in one place, helping teams stay organized, locate files quickly, and protect important creative assets.",
    features: ["Brand kit hub", "Campaign organization", "Agency file sharing", "Version history"],
    linkText: "Protect your brand",
    link: "/cloud-storage-for-brand-creatorss",
    Text: "Learn more",
    image: { src: "/images/solution/spacebyte-for-brand-creators.webp", alt: "Brand creators managing logos and marketing assets with SpaceByte cloud storage" },
    imageTagIcon: { bg: "#fff1f2", stroke: "#e11d48", icon: <><circle cx="12" cy="12" r="10"/><path d="M8.56 2.75c4.37 6.03 6.02 9.42 8.03 17.72"/></> },
    imageTagText: "Brand Asset Hub",
    imageTagSub: "All in one place",
  },
];

/* ─── Helpers ─── */
const ArrowRight = () => (
  <svg viewBox="0 0 24 24" stroke="currentColor">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const Checkmark = () => (
  <span className="sb-chk">
    <svg viewBox="0 0 12 12">
      <polyline points="2,6 5,9 10,3" />
    </svg>
  </span>
);

const sidebarNavRef = useRef<HTMLUListElement>(null);

  /* Inject CSS */
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  /* Scroll reveal */
  useEffect(() => {
    const reveals = document.querySelectorAll<HTMLElement>(".sb-reveal");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  /* Scroll spy */
  useEffect(() => {
    const sections = document.querySelectorAll<HTMLElement>(".sb-solution-block");
    const sideLinks = document.querySelectorAll<HTMLAnchorElement>("#sbSidebarNav a");

    const spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            sideLinks.forEach((a) => {
              a.classList.toggle("active", a.getAttribute("href") === `#${id}`);
            });
          }
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    sections.forEach((s) => spy.observe(s));
    return () => spy.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
  const section = document.getElementById(id);
  if (section) {
    section.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
};
  
  return (
    <Fragment>

      <Helmet>
        <title> Secure Cloud Storage Solutions</title>
        <meta
          name="description"
          content="Explore reliable cloud storage solutions to securely store, manage, and access files. SpaceByte helps creators and businesses keep data organized."
        />
        <meta name="keywords" content="Cloud Storage Solutions" />
        <link rel="canonical" href="https://spacebyte.in/cloud-storage-solutions" />
      </Helmet>

      <LandingNavbar
        color="transparent"
        className="fixed top-0 z-50 w-full flex-shrink-0 border-none bg-light-primary shadow-lg"
        menuPosition="homepage-navbar"
      />
      
      <section className=" relative overflow-hidden !bg-gradient-to-r !from-[#0072ff]/8 !to-[#00c6ff]/8 py-24 md:py-32">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1E3A8A" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div
          className={`landing-container z-10 flex flex-col items-center justify-between pb-40 pt-100 lg:flex-row`}
        >
          <div className="mx-auto w-full max-w-6xl px-4 text-center">

            {/* Heading */}
            <h1 className="font-archivo text-[40px] md:text-[60px] font-bold leading-[1.2] tracking-[-0.02em] text-[#1f2937]">
              Best Cloud Storage    {" "} 
              <br />  
                <span className="color-primary-gradient"> Solutions </span>
            </h1>
                    {/* Sub heading */}
            <p className="mx-auto mt-30 max-w-3xl font-inter text-[18px] md:text-[20px]  text-[#6b7280]">
              Securely store, manage, and access files with SpaceByte cloud storage for creators and businesses.
            </p>
          <div className="my-36 pb-20 flex flex-col items-center justify-center gap-6 sm:flex-row">
            <a
              href="/register"
              className="inline-flex items-center justify-center 
              px-26 py-10 
              text-white font-bold
              bg-gradient-to-r from-[#0072ff] to-[#00c6ff]
              shadow-lg transition-all duration-300
              hover:from-[#0059d6] hover:to-[#00aaff]
              hover:shadow-2xl hover:-translate-y-1"
              style={{
              borderRadius: "12px"
            }}
            >
              Get 25 GB FREE
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center justify-center
              px-36 py-8 font-bold
              text-[#0072ff]
              relative transition-all duration-300
              hover:-translate-y-1 hover:shadow-lg "
              style={{
                borderRadius: "12px",
                background: "linear-gradient(#e5f5fb, #e5f5fb) padding-box, linear-gradient(90deg, #0072ff, #00c6ff) border-box",
                border: "2px solid transparent"
              }}
              >
              View Pricing
            </a>
          </div>
          </div>
        </div>
      </section>    

          <>
      {/* PAGE BODY */}
      <div className="sb-page-body">

        {/* SIDEBAR */}
        <aside className="sb-sidebar">
          <div className="sb-sidebar-heading">All Solutions</div>
          <ul className="sb-sidebar-nav" id="sbSidebarNav" ref={sidebarNavRef}>
            {navItems.map((item) => (
              <li key={item.href}>
                <a  onClick={() => scrollToSection(item.href.replace("#", ""))} className={item.href === "#content-creators" ? "active" : ""}>
                  <span className="sb-nav-icon" style={{ background: item.iconBg }}>
                    <svg viewBox="0 0 24 24" stroke={item.iconStroke}>
                      {item.icon}
                    </svg>
                  </span>
                  {item.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="sb-sidebar-cta">
            {/* <div className="icon">☁️</div> */}
            <h4>Start for Free</h4>
            <p>25 GB of secure cloud storage. No credit card needed.</p>
            <a href="#">Get Started →</a>
          </div>
        </aside>

        {/* MAIN */}
        <main className="sb-main">

          <div className="sb-section-intro sb-reveal">
            <span className="sb-section-label">Solutions by Use Case</span>
            <h2>Cloud Storage  <span className="color-primary-gradient"> That Works for You</span></h2>
            <p>
              Creative professionals and businesses manage large digital data every day. Space Byte offers cloud storage solutions to help videographers, photographers, YouTubers, creators, and marketers securely store, manage, and access their files.
            </p>
          </div>

          {solutions.map((sol, idx) => (
            <div key={sol.id}>
              <section className="sb-solution-block" id={sol.id}>
                <div className={`sb-sol-inner sb-reveal${sol.reverse ? " reverse" : ""}`}>
                  {/* Content */}
                  <div className="sb-sol-content">
                    <span className="sb-sol-tag">{sol.tag}</span>
                    <h2>{sol.title}</h2>
                    <div className="sb-sol-divider" />
                    <p>{sol.body}</p>
                    <a href={sol.link} className="sb-sol-link">
                      {sol.Text}
                      <ArrowRight />
                    </a> <br />
                    <a href="/register" className="sb-sol-link">
                      {sol.linkText}
                      <ArrowRight />
                    </a>
                  </div>

                  {/* Image */}
                  <div className="sb-sol-image">
                    <img src={sol.image.src} alt={sol.image.alt} loading="lazy" />
                    <div className="sb-sol-overlay" />
                    <div className="sb-sol-image-tag">
                      <div className="sb-tag-icon" style={{ background: sol.imageTagIcon.bg }}>
                        <svg width="16" height="16" fill="none" stroke={sol.imageTagIcon.stroke} strokeWidth="2" viewBox="0 0 24 24">
                          {sol.imageTagIcon.icon}
                        </svg>
                      </div>
                      <div>
                        <div className="sb-tag-text">{sol.imageTagText}</div>
                        <div className="sb-tag-sub">{sol.imageTagSub}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {idx < solutions.length - 1 && <hr className="sb-block-divider" />}
            </div>
          ))}
        </main>
      </div>
    </>
    
      {/* <section className="bg-white py-[80px] relative overflow-hidden ">
      <div className="landing-container z-10"> */}
        {/* <SpaceByteSolutions /> */}
      {/* </div>
      </section> */}



      <section className="w-full px-4 md:px-10 py-40 bg-white relative overflow-hidden">
        <div className="landing-container min-h-[280px] md:min-h-[380px] flex items-center justify-center relative rounded-2xl overflow-hidden">

          {/* Video Background */}
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/images/LandingImg/video.mp4"
            autoPlay
            loop
            muted
            playsInline
          />

          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/60" />

          {/* Gradient Light */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-transparent to-blue-600/20" />

          {/* Content */}
          <div className="relative z-10 text-center max-w-4xl px-4">
            <h2 className="text-white text-3xl md:text-5xl font-semibold">
              Your Data, Your Control, Your Cloud
            </h2>

            <p className="text-white/80 text-base md:text-lg mt-20">
              A smarter cloud storage platform that gives you complete control over your data, file sharing, and digital privacy.

            </p>

            <div className="mt-20">
              <a href="/contact" className="btn bg-white text-black px-20 py-10 rounded-xl font-medium hover:scale-105 transition">
                <span>Contact Us</span>
              </a>
            </div>
          </div>

        </div>
      </section>

      <section className="py-20 md:py-32 bg-gradient-to-br from-[#1E3A8A] via-[#3B82F6] to-[#60A5FA] relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 landing-container z-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cta-grid)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-40 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="inline-flex items-center justify-center w-60 h-60 bg-white/20 backdrop-blur-sm rounded-2xl mb-12"
            >
              <Sparkles className="w-30 h-30 text-white" />
            </motion.div>

            {/* Heading */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white"
            >
              Ready to Secure Your Data?
            </motion.h2>

            {/* Subheading */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-lg sm:text-xl text-white/90 my-16 max-w-2xl mx-auto"
            >
              Join thousands of businesses trusting secure cloud storage to protect critical data.

            </motion.p>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-10 justify-center items-center"
            >
              <a href="/register" className="group bg-white text-[#1E3A8A] px-16 py-14 rounded-xl font-semibold hover:bg-gray-100 transition-all hover:shadow-2xl hover:shadow-white/20 transform hover:-translate-y-1 flex items-center gap-2 btn">
                Get 25 GB FREE - No Card Needed
              </a>

            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="mt-20 flex flex-wrap justify-center items-center gap-8 text-white/80 text-sm"
            >

            </motion.div>
          </motion.div>
        </div>
      </section>

      <footer className="Footer-section relative overflow-hidden bg-[#0F172A] text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-4 lg:col-span-2">
              <div className="space-y-4 mr-12 padding-logo padding-top">
                <a href="https://spacebyte.in/" className="flex-shrink-0">
                  <img
                    src="/images/logo-light.png"
                    alt="Logo"
                    className="site-logo"
                  />
                </a>
                <div>
                  <p className="font-medium pt-8 pb-10">SPACEBYTE CLOUD STORAGE</p>
                  <p className="pb-10 pt-3 flex items-center gap-2">
                    311, Maruti Plaza, Sardar Patel Chowk, Krishnanagar, Ahmedabad, Gujarat,
                    India - 382345</p>
                </div>
                <div>
                  <p className="font-bold text-base pb-10 pt-3">FOLLOW US FOR 'SPACEBYTE.IN'</p>
                  <div className="flex gap-4 mt-3 logg mb-10">
                    <ul className="flex space-x-16 pt-3 pr-10">
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.facebook.com/profile.php?id=61567307277313&mibextid=ZbWKwL">
                          <FacebookIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.instagram.com/spacebyte.in?igsh=MmZidnprMXZtbTho ">
                          <InstagramIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://youtube.com/@spacebytecloudstorage?si=lt2mfOM7kSaN3g6B">
                          <YoutubeIcon className="text-white" />
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start ">Product</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="#features" className="hover:text-white transition-colors text-start">
                    Features
                  </a>
                </li>
                <li>
                  <a href="/Pricing" className="hover:text-white transition-colors">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#security" className="hover:text-white transition-colors">
                    Security
                  </a>
                </li>
                <li>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start">Company</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="/about-us" className="hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="/pages/articles" className="hover:text-white transition-colors">
                    Articles
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start">Resources</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="#faq" className="hover:text-white transition-colors">
                    FAQs
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Help Center
                  </a>
                </li>
                {/* <li>
                <a href="https://spacebyte.in/api-docs" className="hover:text-white transition-colors">
                  Developers
                </a>
              </li> */}
              </ul>
            </div>

            {/* Legal Column */}
            <div className='py-8 md:py-20 px-30'>
              <h3 className="font-semibold text-xl mb-10  text-start">Legal</h3>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="/pages/privacy-policy" className="hover:text-white transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/pages/terms-of-service" className="hover:text-white transition-colors">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="font-inter border-t border-t-primary/80 py-16 text-center mt-5">
            <p className="text-sm">
              Copyright © {new Date().getFullYear()}{' '}
              <a href="/login" className="color-primary-gradient">
                SpaceByte Cloud Storage
              </a>
              , All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </Fragment>
  );
}

